import { IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { InputText } from 'app/components/elements/InputText';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { FormCreateUser, HEADER_USER_PAGE, Role, User } from '../type';
import { PagingInfo } from 'types/paging';
import { TableCustom } from 'app/components/elements/TableCustom';
import { includes, startCase } from 'lodash';
import { ModalAddUser } from './ModalAddUser';
import {
  adminBlockUser,
  adminCreateUser,
  adminDeleteUser,
  adminUpdateUser,
  createProfilePic,
  deletePicProfile,
  PayloadCreateUSer
} from 'services/admin';
import { useFlash } from 'contexts/flash';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { SelectOptions } from 'app/components/elements/InputSelect';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { useAuth } from 'contexts/auth';

type RenderDataMapProps = {
  clientAccess: User[];
  onClickEditUser: (user: User) => void;
  onClickDeleteUser: (user: User) => void;
  handleLockUser: (user: User) => void;
  auth: User | null;
};
const renderDataMap = ({
  clientAccess,
  onClickEditUser,
  onClickDeleteUser,
  auth,
  handleLockUser
}: RenderDataMapProps) => {
  return clientAccess.map((user) => {
    return {
      name: {
        value: (
          <Stack flexDirection={'row'} alignItems={'center'} gap={'10px'}>
            {user.photo.url ? (
              <img height={'20px'} src={user.photo.url} alt={`profile-img`} />
            ) : (
              <img height={'20px'} src={'/icons/icon_user_image.svg'} alt={`profile-img`} />
            )}
            <TextEllipsis text={user.name} width={'95%'} />
          </Stack>
        )
      },
      email: {
        value: <TextEllipsis text={user.email} width={'95%'} />
      },
      phone: {
        value: <TextEllipsis text={user.phone || '-'} width={'95%'} />
      },
      role: {
        value: <Typography>{user.role.replace('_', ' ').toUpperCase()}</Typography>
      },
      allowedClientAccess: {
        value: (
          <TextEllipsis
            text={user.accessibleClients?.map((item) => item.clientName).join(', ') || ''}
            width={'95%'}
          />
        )
      },
      viewAccess: {
        value: (
          <TextEllipsis
            text={
              [Role.SUPER_ADMIN, Role.ADMIN].includes(user.role)
                ? 'All'
                : user?.viewAccess
                  ? user.viewAccess.map((item) => startCase(item.toLowerCase())).join(', ')
                  : ''
            }
            width={'95%'}
          />
        )
      },
      editAccess: {
        value: (
          <TextEllipsis
            text={
              [Role.SUPER_ADMIN, Role.ADMIN].includes(user.role)
                ? 'All'
                : user?.editAccess
                  ? user.editAccess.map((item) => startCase(item.toLowerCase())).join(', ')
                  : ''
            }
            width={'95%'}
          />
        )
      },
      actions: {
        value: (
          <Stack flexDirection={'row'} gap={'10px'} alignItems={'center'}>
            {auth?.role === Role.SUPER_ADMIN && (
              <>
                {user.lockedAt ? (
                  <img
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      handleLockUser(user);
                    }}
                    src={'/icons/icon_lock.svg'}
                    alt={`icon_lock`}
                  />
                ) : (
                  <img
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      handleLockUser(user);
                    }}
                    src={'/icons/icon_action_lock.svg'}
                    alt={`icon_lock`}
                  />
                )}
              </>
            )}
            {(user?.role !== Role.SUPER_ADMIN || auth?.role === Role.SUPER_ADMIN) && (
              <img
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (user?.id) {
                    onClickEditUser(user);
                  }
                }}
                src={'/icons/icon_action_edit.svg'}
                alt={`icon_action_edit`}
              />
            )}
            {auth?.role === Role.SUPER_ADMIN && (
              <img
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (user?.id) {
                    onClickDeleteUser(user);
                  }
                }}
                src={'/icons/icon_action_delete.svg'}
                alt={`icon_action_delete`}
              />
            )}
          </Stack>
        )
      }
    };
  });
};

type ClientsAccessTabProps = {
  clientAccess: User[];
  pagingInfo: PagingInfo;
  handleChangePage: (value: number) => void;
  handleSort: (orderBy: string) => void;
  sortOption: { field?: string; order?: string };
  onSearch: (value: string) => void;
  optionClient: SelectOptions[];
  handleRefreshDataClient: () => void;
};
export const ClientsAccessTab = ({
  clientAccess,
  optionClient,
  pagingInfo,
  handleChangePage,
  handleSort,
  sortOption,
  onSearch,
  handleRefreshDataClient
}: ClientsAccessTabProps) => {
  const { setFlash } = useFlash();
  const { auth } = useAuth();
  const [showModalAddUser, setShowModalAddUser] = useState<boolean>(false);
  const [showModalEditUser, setShowModalEditUser] = useState<boolean>(false);
  const [showModalDeleteUser, setShowModalDeleteUser] = useState<boolean>(false);
  const [showModalLockUser, setShowModalLockUser] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [clientEdit, setClientEdit] = useState<User>();
  const handleAddUser = useCallback(
    async (user: FormCreateUser) => {
      setShowModalAddUser(false);
      const isGlobalAdmin = includes([Role.SUPER_ADMIN, Role.ADMIN], user.role);
      const { clientIds, viewAccess, editAccess, phone, ...userFields } = user;
      const payload: PayloadCreateUSer = {
        ...userFields,
        phone: phone?.trim() || null,
        ...(isGlobalAdmin ? {} : { clientIds: clientIds?.map((item) => Number(item)) }),
        viewAccess: {
          activities: includes(viewAccess, 'activities'),
          issues: includes(viewAccess, 'issues'),
          reports: includes(viewAccess, 'reports'),
          documentation: includes(viewAccess, 'documentation'),
          validation: includes(viewAccess, 'validation'),
          maintenance: includes(viewAccess, 'maintenance')
        },
        editAccess: {
          activities: includes(editAccess, 'activities'),
          issues: includes(editAccess, 'issues'),
          reports: includes(editAccess, 'reports'),
          documentation: includes(editAccess, 'documentation'),
          validation: includes(editAccess, 'validation'),
          maintenance: includes(editAccess, 'maintenance')
        }
      };
      const { data } = await adminCreateUser(payload);
      if (user.file) {
        const formData = new FormData();
        const blob = new Blob([user?.file], { type: user.file?.type });
        formData.append('file', blob, user.file?.name);
        await createProfilePic(data.user.id, formData);
      }
      setFlash({ type: 'success', message: 'Add user successful' });
      handleRefreshDataClient();
    },
    [setFlash, handleRefreshDataClient]
  );

  const handleEditUser = useCallback(
    async (user: FormCreateUser) => {
      if (!clientEdit?.id) return;
      setShowModalEditUser(false);
      const isGlobalAdmin = includes([Role.SUPER_ADMIN, Role.ADMIN], user.role);
      const { clientIds, viewAccess, editAccess, phone, ...userFields } = user;
      const payload: PayloadCreateUSer = {
        ...userFields,
        phone: phone?.trim() || null,
        ...(isGlobalAdmin ? {} : { clientIds: clientIds?.map((item) => Number(item)) }),
        viewAccess: {
          activities: includes(viewAccess, 'activities'),
          issues: includes(viewAccess, 'issues'),
          reports: includes(viewAccess, 'reports'),
          documentation: includes(viewAccess, 'documentation'),
          validation: includes(viewAccess, 'validation'),
          maintenance: includes(viewAccess, 'maintenance')
        },
        editAccess: {
          activities: includes(editAccess, 'activities'),
          issues: includes(editAccess, 'issues'),
          reports: includes(editAccess, 'reports'),
          documentation: includes(editAccess, 'documentation'),
          validation: includes(editAccess, 'validation'),
          maintenance: includes(editAccess, 'maintenance')
        }
      };
      const formData = new FormData();
      const blob = new Blob([user.file], { type: user.file?.type });
      formData.append('file', blob, user.file?.name);
      await adminUpdateUser(Number(clientEdit.id), payload);
      if (user.file) {
        const formData = new FormData();
        const blob = new Blob([user?.file], { type: user.file?.type });
        formData.append('file', blob, user.file?.name);
        await createProfilePic(Number(clientEdit.id), formData);
      }
      if (!user.url && user.photoId) {
        await deletePicProfile(user.photoId);
      }
      setFlash({ type: 'success', message: 'Edit user successful' });
      handleRefreshDataClient();
    },
    [clientEdit?.id, setFlash, handleRefreshDataClient]
  );

  const handleLockUser = useCallback(async () => {
    if (!clientEdit?.id) return;
    await adminBlockUser(Number(clientEdit.id));
    setShowModalLockUser(false);
    setFlash({
      type: 'success',
      message: clientEdit.lockedAt ? 'Unlock user successful' : 'Lock user successful'
    });
    handleRefreshDataClient();
  }, [clientEdit, handleRefreshDataClient, setFlash]);

  const handleDeleteUser = useCallback(async () => {
    if (!clientEdit?.id) return;
    setShowModalDeleteUser(false);
    await adminDeleteUser(Number(clientEdit.id));
    setFlash({ type: 'success', message: 'Edit user successful' });
    handleRefreshDataClient();
  }, [clientEdit?.id, setFlash, handleRefreshDataClient]);

  const { asyncCallback: asyncAddUser } = useAsyncCallback(handleAddUser, []);
  const { asyncCallback: asyncLockUser } = useAsyncCallback(handleLockUser, []);
  const { asyncCallback: asyncEditUser } = useAsyncCallback(handleEditUser, []);
  const { asyncCallback: asyncDeleteUser } = useAsyncCallback(handleDeleteUser, []);

  return (
    <Stack sx={{ marginTop: '30px' }}>
      <Stack
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px'
        }}>
        <Stack flexDirection={'row'} gap={1.25}>
          <Stack gap={1}>
            <InputText
              startAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <img alt={'icon-user'} src={'/icons/icon_search.svg'} width={24} />
                  </IconButton>
                </InputAdornment>
              }
              placeholder={'Search'}
              inputProps={{
                value: searchText,
                style: {
                  width: '352px',
                  minHeight: '46px',
                  height: '46px',
                  padding: 0
                },
                onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setSearchText(event.target.value);
                },
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  if (event.key === 'Enter') {
                    onSearch && onSearch(searchText);
                  }
                }
              }}
            />
          </Stack>
          <ButtonCustom
            sx={{ width: 'max-content', height: '46px' }}
            label={'Search'}
            onClick={() => onSearch(searchText)}
          />
        </Stack>
        <ButtonCustom
          sx={{ width: 'max-content', height: '46px' }}
          label={'Add User'}
          onClick={() => setShowModalAddUser(true)}
        />
      </Stack>
      <Stack sx={{ marginTop: '30px', marginBottom: '30px' }}>
        <TableCustom
          onSortHeader={handleSort}
          sort={sortOption}
          data={renderDataMap({
            auth,
            clientAccess,
            onClickEditUser: (user: User) => {
              setClientEdit(user);
              setShowModalEditUser(true);
            },
            onClickDeleteUser: (user: User) => {
              setClientEdit(user);
              setShowModalDeleteUser(true);
            },
            handleLockUser: (user: User) => {
              setClientEdit(user);
              setShowModalLockUser(true);
            }
          })}
          headers={HEADER_USER_PAGE}
          handleChooseRow={() => {}}
          styledTableRow={{ cursor: ' default ' }}
          pagingInfo={pagingInfo}
          handleChangePage={handleChangePage}
        />
      </Stack>
      <ModalAddUser
        optionClient={optionClient}
        onSubmit={asyncAddUser}
        isOpen={showModalAddUser}
        onCloseModal={() => setShowModalAddUser(false)}
      />
      <ModalAddUser
        isEdit
        initData={clientEdit}
        optionClient={optionClient}
        onSubmit={asyncEditUser}
        isOpen={showModalEditUser}
        onCloseModal={() => setShowModalEditUser(false)}
      />
      <DialogConfirm
        isOpen={showModalDeleteUser}
        onClose={() => setShowModalDeleteUser(false)}
        textTitle={'Delete User'}
        textButton={'Delete User'}
        textCancel={'Cancel'}
        isDelete
        isIconClose
        textContent={'Are you sure you want to delete this user and all data?'}
        onSubmit={asyncDeleteUser}
      />
      <DialogConfirm
        isOpen={showModalLockUser}
        onClose={() => setShowModalLockUser(false)}
        textTitle={clientEdit?.lockedAt ? 'Unlock User' : 'Lock User'}
        textButton={clientEdit?.lockedAt ? 'Unlock User' : 'Lock User'}
        textCancel={'Cancel'}
        isIconClose
        textContent={
          clientEdit?.lockedAt
            ? 'Are you sure you want to unlock this user?'
            : 'Are you sure you want to lock this user?'
        }
        onSubmit={asyncLockUser}
      />
    </Stack>
  );
};
